/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import * as AppPropTypes from '../../lib/PropTypes';

import CardVisual from './CardVisual';
import Link from './Link';

import styles from '../../../styles/partials/card-layout.module.scss';

const propTypes = {
    type: PropTypes.bool,
    image: AppPropTypes.image,
    url: PropTypes.string,
    external: PropTypes.bool,
    tags: PropTypes.node,
    tagsPosition: PropTypes.oneOf(['left', 'right']),
    pillContent: PropTypes.node,
    pillPosition: PropTypes.oneOf(['left', 'right']),
    buttons: PropTypes.node,
    onClick: PropTypes.func,
    className: PropTypes.string,
    withoutPlaceholder: PropTypes.bool,
    placeholderIndex: PropTypes.number,
    isStacked: PropTypes.bool, // always stacked the card 'image' on top of the 'content'
    isWide: PropTypes.bool, // this card has a lot of content and therefore should "stack" sooner (~1400px media query)
    isShort: PropTypes.bool, // this is shorter than usual; it has a 'visual' with a different height
    isDark: PropTypes.bool,
    children: PropTypes.node,
};

const defaultProps = {
    type: null,
    image: null,
    url: null,
    external: false,
    tags: null,
    tagsPosition: 'left',
    pillContent: null,
    pillPosition: 'right',
    buttons: null,
    onClick: null,
    className: null,
    withoutPlaceholder: false,
    placeholderIndex: null,
    isStacked: false,
    isWide: false,
    isShort: false,
    isDark: false,
    children: null,
};

const CardLayout = ({
    type,
    image,
    url,
    external,
    tags,
    tagsPosition,
    pillContent,
    pillPosition,
    buttons,
    onClick,
    className,
    withoutPlaceholder,
    placeholderIndex,
    isStacked,
    isWide,
    isShort,
    isDark,
    children,
}) => {
    const color = useMemo(() => {
        // return 'green';
        switch (type) {
            case 'news':
            case 'press':
            case 'article':
                return 'black';
            case 'event':
                return 'orange';
            case 'film':
                return 'green';
            case 'section':
                return 'yellow';
            default:
                return 'green';
        }
    }, [type]);

    const inner = useMemo(
        () =>
            url !== null ? (
                <Link
                    className={classNames(styles.visual, styles.visualLink)}
                    href={url}
                    external={external}
                    onClick={onClick}
                >
                    <CardVisual
                        imageWrapClassName={styles.imageWrap}
                        imageClassName={styles.image}
                        color={color}
                        image={image}
                        tagComponents={tags}
                        tagsPosition={tagsPosition}
                        pillContent={pillContent}
                        pillPosition={pillPosition}
                        placeholderIndex={placeholderIndex}
                    />
                </Link>
            ) : (
                <CardVisual
                    className={styles.visual}
                    color={color}
                    image={image}
                    tagComponents={tags}
                    tagsPosition={tagsPosition}
                    pillContent={pillContent}
                    pillPosition={pillPosition}
                    placeholderIndex={placeholderIndex}
                />
            ),
        [url, external, onClick, color, image, tags, tagsPosition, pillContent, pillPosition],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.hasImage]: true,
                    [styles.isStacked]: isStacked,
                    [styles.isWide]: isWide,
                    [styles.isShort]: isShort,
                    [styles.isDark]: isDark,
                    [styles[type]]: type !== null,
                },
            ])}
        >
            {!withoutPlaceholder ? inner : null}
            <div className={styles.content}>
                <div className={styles.main}>{children}</div>
                {buttons !== null ? <div className={styles.buttons}>{buttons}</div> : null}
            </div>
        </div>
    );
};
CardLayout.propTypes = propTypes;
CardLayout.defaultProps = defaultProps;

export default CardLayout;
